import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import {
    Loader2, Inbox, ShieldAlert, ChevronRight, Mail, Phone, MapPin, Building2,
    Calendar, Zap, Flame, Save, ListChecks, FileDown, Sparkles, ArrowRightCircle, CheckCircle2,
} from "lucide-react";

import { API } from "@/lib/api";

const STATUS_FLOW = [
    { value: "new", label: "Nouveau", color: "bg-blue-500" },
    { value: "contacted", label: "Contacté", color: "bg-amber-500" },
    { value: "quote_sent", label: "Devis envoyé", color: "bg-violet-500" },
    { value: "won", label: "Gagné", color: "bg-emerald-500" },
    { value: "lost", label: "Perdu", color: "bg-rose-500" },
];

function StatusBadge({ status }) {
    const conf = STATUS_FLOW.find((s) => s.value === status) || STATUS_FLOW[0];
    return (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold text-white ${conf.color}`} data-testid={`status-badge-${status}`}>
            <span className="h-1.5 w-1.5 rounded-full bg-white" />
            {conf.label}
        </span>
    );
}

export default function AdminQuotes() {
    const { user, loading: authLoading } = useAuth();
    const [stats, setStats] = useState(null);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState("all");
    const [filterAudience, setFilterAudience] = useState("all");
    const [selectedQuote, setSelectedQuote] = useState(null);
    const [newStatus, setNewStatus] = useState("new");
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);
    const [ceeAnalysis, setCeeAnalysis] = useState(null);
    const [analyzing, setAnalyzing] = useState(false);
    const [converting, setConverting] = useState(false);

    const loadData = async () => {
        setLoading(true);
        try {
            const [s, q] = await Promise.all([
                api.get("/admin/quote-requests/stats"),
                api.get("/admin/quote-requests", {
                    params: {
                        ...(filterStatus !== "all" && { status: filterStatus }),
                        ...(filterAudience !== "all" && { audience: filterAudience }),
                    },
                }),
            ]);
            setStats(s.data);
            setItems(q.data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user?.role === "admin") loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, filterStatus, filterAudience]);

    const openDetail = (q) => {
        setSelectedQuote(q);
        setNewStatus(q.status || "new");
        setNotes("");
        setCeeAnalysis(null);
    };

    const analyzeCee = async () => {
        if (!selectedQuote) return;
        setAnalyzing(true);
        try {
            const res = await api.post(`/admin/quote-requests/${selectedQuote.id}/analyze-cee`);
            setCeeAnalysis(res.data);
            toast.success("Analyse effectuée");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setAnalyzing(false);
        }
    };

    const convertToCee = async () => {
        if (!selectedQuote) return;
        setConverting(true);
        try {
            const res = await api.post(`/admin/quote-requests/${selectedQuote.id}/convert-to-cee`);
            toast.success(`Lead CEE créé : ${res.data.company}`);
            setSelectedQuote(null);
            await loadData();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setConverting(false);
        }
    };

    const updateStatus = async () => {
        if (!selectedQuote) return;
        setSaving(true);
        try {
            await api.patch(`/admin/quote-requests/${selectedQuote.id}/status`, {
                status: newStatus,
                admin_notes: notes || null,
            });
            toast.success(`Statut mis à jour : ${newStatus}`);
            setSelectedQuote(null);
            await loadData();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setSaving(false);
        }
    };

    if (authLoading) {
        return (
            <div className="em-container py-32 text-center text-muted-foreground" data-testid="admin-auth-loading">
                <Loader2 className="h-8 w-8 mx-auto animate-spin" />
            </div>
        );
    }

    if (!user || user.role !== "admin") {
        return (
            <div className="em-container py-20" data-testid="admin-forbidden">
                <ShieldAlert className="h-12 w-12 text-destructive" />
                <h1 className="font-display text-4xl font-black text-primary mt-4">Accès refusé</h1>
                <p className="text-muted-foreground mt-3 max-w-2xl">
                    Cette page est réservée aux administrateurs Energy Market.
                </p>
                <Button asChild className="rounded-full mt-6 bg-primary hover:bg-primary/90">
                    <Link to="/">Retour à l'accueil</Link>
                </Button>
            </div>
        );
    }

    return (
        <div className="em-container py-8" data-testid="admin-quotes-page">
            <div className="flex flex-wrap items-end justify-between gap-3 mb-2">
                <div>
                    <p className="em-eyebrow flex items-center gap-2"><ListChecks className="h-3.5 w-3.5" />Administration</p>
                    <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-primary mt-2">Devis entrants</h1>
                </div>
                <div className="flex gap-2">
                    <Button
                        onClick={async () => {
                            try {
                                const res = await api.get("/admin/quote-requests/export.csv", { responseType: "blob" });
                                const url = window.URL.createObjectURL(res.data);
                                const a = document.createElement("a");
                                a.href = url; a.download = "energy-market-devis.csv";
                                document.body.appendChild(a); a.click(); a.remove();
                                window.URL.revokeObjectURL(url);
                                toast.success("Export CSV téléchargé");
                            } catch (e) {
                                toast.error("Erreur d'export");
                            }
                        }}
                        type="button"
                        variant="outline"
                        className="rounded-full"
                        data-testid="export-quotes-csv"
                    >
                        <FileDown className="h-4 w-4 mr-2" />CSV
                    </Button>
                    <Button
                        onClick={async () => {
                            try {
                                const res = await api.get("/admin/quote-requests/export.xlsx", { responseType: "blob" });
                                const url = window.URL.createObjectURL(res.data);
                                const a = document.createElement("a");
                                a.href = url; a.download = "energy-market-devis.xlsx";
                                document.body.appendChild(a); a.click(); a.remove();
                                window.URL.revokeObjectURL(url);
                                toast.success("Export Excel téléchargé");
                            } catch (e) {
                                toast.error("Erreur d'export");
                            }
                        }}
                        type="button"
                        className="rounded-full bg-primary hover:bg-primary/90 font-bold"
                        data-testid="export-quotes-xlsx"
                    >
                        <FileDown className="h-4 w-4 mr-2" />Excel
                    </Button>
                </div>
            </div>
            <p className="text-muted-foreground mt-3 max-w-2xl">Pilotez le funnel commercial : suivi, statut et conversion des demandes de devis.</p>

            {/* STATS */}
            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mt-10" data-testid="admin-stats">
                    <Card className="rounded-2xl border-border md:col-span-2">
                        <CardContent className="p-5 flex items-center gap-3">
                            <div className="h-12 w-12 rounded-xl bg-accent flex items-center justify-center"><Inbox className="h-6 w-6 text-accent-foreground" /></div>
                            <div>
                                <p className="em-eyebrow">Total</p>
                                <p className="font-display text-3xl font-black text-primary">{stats.total}</p>
                            </div>
                        </CardContent>
                    </Card>
                    {STATUS_FLOW.map((s) => (
                        <Card key={s.value} className="rounded-2xl border-border" data-testid={`admin-stat-${s.value}`}>
                            <CardContent className="p-4">
                                <div className="flex items-center gap-2">
                                    <span className={`h-2 w-2 rounded-full ${s.color}`} />
                                    <p className="text-xs em-eyebrow">{s.label}</p>
                                </div>
                                <p className="font-display text-2xl font-black text-primary mt-1">{stats.by_status[s.value] || 0}</p>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* FILTERS */}
            <div className="mt-8 flex flex-wrap gap-3 items-center" data-testid="admin-filters">
                <Tabs value={filterStatus} onValueChange={setFilterStatus}>
                    <TabsList className="rounded-full">
                        <TabsTrigger value="all" className="rounded-full" data-testid="filter-status-all">Tous</TabsTrigger>
                        {STATUS_FLOW.map((s) => (
                            <TabsTrigger key={s.value} value={s.value} className="rounded-full" data-testid={`filter-status-${s.value}`}>
                                {s.label}
                            </TabsTrigger>
                        ))}
                    </TabsList>
                </Tabs>
                <Select value={filterAudience} onValueChange={setFilterAudience}>
                    <SelectTrigger className="rounded-full w-[180px]" data-testid="filter-audience">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Toutes audiences</SelectItem>
                        <SelectItem value="particulier">Particuliers</SelectItem>
                        <SelectItem value="entreprise">Entreprises</SelectItem>
                    </SelectContent>
                </Select>
                <span className="text-sm text-muted-foreground ml-2">{items.length} résultat(s)</span>
            </div>

            {/* TABLE */}
            <Card className="rounded-3xl border-border mt-6">
                <CardContent className="p-0">
                    {loading ? (
                        <div className="py-20 text-center text-muted-foreground" data-testid="admin-loading">
                            <Loader2 className="h-8 w-8 mx-auto animate-spin" />
                        </div>
                    ) : items.length === 0 ? (
                        <div className="py-16 text-center text-muted-foreground" data-testid="admin-empty">
                            <Inbox className="h-12 w-12 mx-auto text-primary/30" />
                            <p className="mt-3">Aucun devis pour ces filtres.</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full min-w-[820px]" data-testid="admin-quotes-table">
                                <thead className="bg-secondary">
                                    <tr className="text-left">
                                        <th className="px-5 py-4 em-eyebrow">Date</th>
                                        <th className="px-5 py-4 em-eyebrow">Demandeur</th>
                                        <th className="px-5 py-4 em-eyebrow">Cible</th>
                                        <th className="px-5 py-4 em-eyebrow">Énergie</th>
                                        <th className="px-5 py-4 em-eyebrow">CP</th>
                                        <th className="px-5 py-4 em-eyebrow">Conso</th>
                                        <th className="px-5 py-4 em-eyebrow">Statut</th>
                                        <th className="px-5 py-4"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((q) => (
                                        <tr key={q.id} className="border-t border-border hover:bg-muted/40 transition-colors" data-testid={`quote-row-${q.id}`}>
                                            <td className="px-5 py-4 text-sm">
                                                {new Date(q.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })}
                                            </td>
                                            <td className="px-5 py-4">
                                                <p className="font-display font-bold flex items-center gap-2">
                                                    {q.full_name}
                                                    {q.cee_interest && (
                                                        <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-wide uppercase bg-accent text-accent-foreground px-2 py-0.5 rounded-full" data-testid={`cee-flag-${q.id}`} title="Client intéressé par les CEE">
                                                            <Sparkles className="h-3 w-3" />CEE
                                                        </span>
                                                    )}
                                                    {q.converted_cee_lead_id && (
                                                        <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-wide uppercase bg-emerald-600 text-white px-2 py-0.5 rounded-full" data-testid={`cee-converted-${q.id}`} title="Devis converti en lead CEE">
                                                            <CheckCircle2 className="h-3 w-3" />Converti
                                                        </span>
                                                    )}
                                                </p>
                                                <p className="text-xs text-muted-foreground">{q.email}</p>
                                            </td>
                                            <td className="px-5 py-4 text-sm capitalize">{q.audience}</td>
                                            <td className="px-5 py-4 text-sm">
                                                <span className="inline-flex items-center gap-1">
                                                    {q.energy_type === "gas" ? <Flame className="h-4 w-4" /> : q.energy_type === "both" ? <><Zap className="h-4 w-4" /><Flame className="h-4 w-4" /></> : <Zap className="h-4 w-4" />}
                                                </span>
                                            </td>
                                            <td className="px-5 py-4 text-sm font-mono">{q.postal_code}</td>
                                            <td className="px-5 py-4 text-sm font-mono">{q.annual_kwh ? `${q.annual_kwh.toLocaleString("fr-FR")} kWh` : "—"}</td>
                                            <td className="px-5 py-4"><StatusBadge status={q.status} /></td>
                                            <td className="px-5 py-4 text-right">
                                                <Button variant="ghost" size="sm" type="button" onClick={() => openDetail(q)} className="rounded-full" data-testid={`open-quote-${q.id}`}>
                                                    Gérer <ChevronRight className="h-4 w-4 ml-1" />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* DETAIL DIALOG */}
            <Dialog open={!!selectedQuote} onOpenChange={(open) => !open && setSelectedQuote(null)}>
                <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl">Détail du devis</DialogTitle>
                    </DialogHeader>
                    {selectedQuote && (
                        <div className="space-y-4" data-testid="quote-detail">
                            <div className="grid grid-cols-2 gap-4">
                                <DetailField icon={Mail} label="Email" value={selectedQuote.email} />
                                <DetailField icon={Phone} label="Téléphone" value={selectedQuote.phone} />
                                <DetailField icon={Building2} label="Société" value={selectedQuote.company || "—"} />
                                <DetailField icon={MapPin} label="Code postal" value={selectedQuote.postal_code} />
                                <DetailField icon={Zap} label="Énergie" value={selectedQuote.energy_type} />
                                <DetailField icon={Calendar} label="Reçu le" value={new Date(selectedQuote.created_at).toLocaleString("fr-FR")} />
                            </div>
                            {selectedQuote.message && (
                                <div className="p-4 bg-secondary rounded-2xl">
                                    <p className="em-eyebrow mb-1">Message</p>
                                    <p className="text-sm">{selectedQuote.message}</p>
                                </div>
                            )}

                            {/* CEE Conversion block */}
                            <div className="rounded-2xl border-2 border-dashed border-primary/30 p-4 bg-accent/5" data-testid="cee-conversion-block">
                                <div className="flex items-start justify-between gap-3 flex-wrap">
                                    <div className="flex-1 min-w-[200px]">
                                        <p className="em-eyebrow flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5 text-primary" />Conversion CEE / Solaire</p>
                                        <p className="text-sm mt-1 text-muted-foreground">
                                            {selectedQuote.converted_cee_lead_id ? (
                                                <span className="text-emerald-700 font-bold inline-flex items-center gap-1"><CheckCircle2 className="h-4 w-4" />Déjà converti en lead CEE.</span>
                                            ) : selectedQuote.cee_interest ? (
                                                <span className="text-primary font-bold">✓ Client opt-in CEE — prêt à convertir.</span>
                                            ) : (
                                                "Analyser le profil pour identifier les opportunités CEE (isolation, pompe à chaleur, solaire…)."
                                            )}
                                        </p>
                                    </div>
                                    {!selectedQuote.converted_cee_lead_id && (
                                        <Button
                                            type="button"
                                            onClick={analyzeCee}
                                            disabled={analyzing}
                                            variant="outline"
                                            className="rounded-full border-primary text-primary hover:bg-primary/10"
                                            data-testid="analyze-cee-btn"
                                        >
                                            {analyzing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                                            {analyzing ? "Analyse…" : "Analyser"}
                                        </Button>
                                    )}
                                </div>

                                {ceeAnalysis && (
                                    <div className="mt-4 space-y-3" data-testid="cee-analysis-result">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span className={`text-xs font-bold uppercase px-2.5 py-1 rounded-full text-white ${
                                                ceeAnalysis.suggestion.priority === "high" ? "bg-rose-600" :
                                                ceeAnalysis.suggestion.priority === "medium" ? "bg-amber-500" : "bg-slate-500"
                                            }`} data-testid="cee-priority">
                                                Priorité {ceeAnalysis.suggestion.priority}
                                            </span>
                                            <span className="text-xs font-bold text-primary bg-accent px-2.5 py-1 rounded-full" data-testid="cee-est-savings">
                                                ~{Math.round(ceeAnalysis.suggestion.estimated_savings_eur_per_year || 0).toLocaleString("fr-FR")} €/an
                                            </span>
                                            {(ceeAnalysis.suggestion.recommended_interests || []).map((i) => (
                                                <span key={i} className="text-xs bg-secondary px-2 py-1 rounded-full capitalize" data-testid={`cee-interest-${i}`}>
                                                    {i.replace("_", " ")}
                                                </span>
                                            ))}
                                        </div>
                                        <p className="text-sm leading-relaxed bg-white border border-border rounded-xl p-3 whitespace-pre-wrap" data-testid="cee-reasoning">
                                            {ceeAnalysis.suggestion.reasoning}
                                        </p>
                                        <Button
                                            type="button"
                                            onClick={convertToCee}
                                            disabled={converting}
                                            className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90 font-bold w-full"
                                            data-testid="convert-to-cee-btn"
                                        >
                                            {converting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ArrowRightCircle className="h-4 w-4 mr-2" />}
                                            {converting ? "Conversion…" : "Convertir en lead CEE"}
                                        </Button>
                                    </div>
                                )}
                            </div>

                            <div className="pt-4 border-t border-border space-y-3">
                                <div>
                                    <p className="em-eyebrow mb-2">Mettre à jour le statut</p>
                                    <Select value={newStatus} onValueChange={setNewStatus}>
                                        <SelectTrigger className="rounded-xl" data-testid="status-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {STATUS_FLOW.map((s) => (
                                                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <p className="em-eyebrow mb-2">Notes internes (optionnel)</p>
                                    <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="rounded-xl" placeholder="Suivi de la conversation, prochaines étapes…" data-testid="admin-notes" />
                                </div>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button onClick={updateStatus} disabled={saving} className="rounded-full bg-primary hover:bg-primary/90" data-testid="save-status">
                            {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sauvegarde…</> : <><Save className="h-4 w-4 mr-2" />Enregistrer</>}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function DetailField({ icon: Icon, label, value }) {
    return (
        <div>
            <p className="em-eyebrow flex items-center gap-1.5"><Icon className="h-3 w-3" />{label}</p>
            <p className="text-sm mt-1 break-words">{value || "—"}</p>
        </div>
    );
}
